import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { 
  FileText, 
  Search, 
  Plus,
  Download,
  Share2,
  Trash2,
  Edit,
  Users,
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Brain,
  Upload
} from 'lucide-react';
import { DatabaseService } from '../lib/database';
import { useUserRole } from '../hooks/useUserRole';
import { useScreenSize } from '../hooks/useScreenSize';
import { useDeal } from '../contexts/DealContext';
import { BulkUploadModal } from './BulkUploadModal';
import type { 
  UserDocument, 
  DocumentType, 
  DocumentAccessLevel
} from '../types/database';
import {
  getDocumentTypeLabel,
  formatFileSize
} from '../types/database';

interface DocumentListPanelProps {
  className?: string;
  buyerId?: string;
  dealId?: string;
  selectedDocument?: UserDocument | null;
  onDocumentSelect: (document: UserDocument | null) => void;
  onUploadClick: () => void;
  onDocumentUpdate: () => void;
  onExtractionClick?: (document: UserDocument) => void;
}

export function DocumentListPanel({ 
  className = '', 
  buyerId,
  dealId,
  selectedDocument,
  onDocumentSelect,
  onUploadClick,
  onDocumentUpdate,
  onExtractionClick
}: DocumentListPanelProps) {
  
  
  const { user } = useUser();
  
  const { isMobile, isTablet } = useScreenSize();
  const { 
    canUploadDocuments, 
    canDownloadDocuments, 
    canShareDocuments, 
    canDeleteDocuments,
    canEditDocuments
  } = useUserRole();
  const { availableDeals } = useDeal();

  const [documents, setDocuments] = useState<UserDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  
  // Multi-select state
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Bulk upload modal state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<DocumentType | 'all'>('all');
  const [filterAccessLevel, setFilterAccessLevel] = useState<DocumentAccessLevel | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<'all' | 'buyer' | 'seller'>('all');
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);

  // Load documents
  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      setError(null);

      let response;
      
      // Use selectedDealId if available, otherwise use dealId prop
      const activeDealId = selectedDealId || dealId;
      
      // If a deal ID is provided, load deal-specific documents
      if (activeDealId) {
        response = await DatabaseService.getDealDocumentDetails(
          activeDealId,
          user?.id // Pass Clerk user ID
        );
        
        // If user doesn't have access to the deal, fall back to showing all documents
        if (response.error && response.error.includes('does not have access')) {
          setMessage('You do not have access to the selected deal. Showing all available documents.');
          response = await DatabaseService.getUserDocuments(
            filterType === 'all' ? undefined : filterType,
            100, // Load more documents
            0,
            user?.id // Pass Clerk user ID
          );
        }
      } else {
        // Otherwise, load all user documents
        response = await DatabaseService.getUserDocuments(
          filterType === 'all' ? undefined : filterType,
          100, // Load more documents
          0,
          user?.id // Pass Clerk user ID
        );
      }

      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        setDocuments(response.data);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load documents';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle document download
  const handleDownload = async (document: UserDocument) => {
    try {
      const response = await DatabaseService.downloadDocument(document.id);
      
      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        // Create a temporary link to download the file
        const link = window.document.createElement('a');
        link.href = response.data.url;
        link.download = response.data.fileName;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        
        setMessage('Document download started');
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to download document';
      setError(errorMessage);
    }
  };

  // Handle single document deletion
  const handleDelete = async (document: UserDocument) => {
    try {
      const response = await DatabaseService.deleteDocument(document.id, user?.id);
      
      if (response.error) {
        setError(response.error);
      } else {
        setMessage('Document deleted successfully');
        await loadDocuments();
        onDocumentUpdate();
        // Clear selection if deleted document was selected
        if (selectedDocument?.id === document.id) {
          onDocumentSelect(null);
        }
        // Remove from multi-select if selected
        setSelectedDocuments(prev => {
          const newSet = new Set(prev);
          newSet.delete(document.id);
          return newSet;
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete document';
      setError(errorMessage);
    }
  };

  // Handle single document editing
  const handleEdit = async (document: UserDocument) => {
    try {
      // For now, just show a simple prompt for editing the title
      const newTitle = prompt('Edit document title:', document.title);
      if (newTitle && newTitle !== document.title) {
        const response = await DatabaseService.updateDocument(document.id, {
          title: newTitle
        }, user?.id);
        
        if (response.error) {
          setError(response.error);
        } else {
          setMessage('Document updated successfully');
          await loadDocuments();
          onDocumentUpdate();
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to edit document';
      setError(errorMessage);
    }
  };

  // Multi-select helper functions
  const handleSelectDocument = (documentId: string, isSelected: boolean) => {
    setSelectedDocuments(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(documentId);
      } else {
        newSet.delete(documentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedDocuments(new Set());
      setSelectAll(false);
    } else {
      const deletableDocIds = filteredDocuments
        .filter(doc => canDeleteDocuments && doc.can_admin)
        .map(doc => doc.id);
      setSelectedDocuments(new Set(deletableDocIds));
      setSelectAll(true);
    }
  };

  // Filter documents based on search and filters
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.file_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || doc.document_type === filterType;
    const matchesAccessLevel = filterAccessLevel === 'all' || doc.access_level === filterAccessLevel;
    
    // Filter by buyer/seller category based on document type
    const isBuyerDoc = ['tax_return', 'bank_statement', 'financial_statement'].includes(doc.document_type);
    const isSellerDoc = ['profit_loss_statement', 'balance_sheet', 'inventory_list', 'lease_agreement'].includes(doc.document_type);
    
    let matchesCategory = true;
    if (filterCategory === 'buyer') {
      matchesCategory = isBuyerDoc;
    } else if (filterCategory === 'seller') {
      matchesCategory = isSellerDoc;
    }
    
    return matchesSearch && matchesType && matchesAccessLevel && matchesCategory;
  });
  

  // Update selectAll state when selectedDocuments changes
  useEffect(() => {
    const deletableDocIds = filteredDocuments
      .filter(doc => canDeleteDocuments && doc.can_admin)
      .map(doc => doc.id);
    const allDeletableSelected = deletableDocIds.length > 0 && 
      deletableDocIds.every(id => selectedDocuments.has(id));
    setSelectAll(allDeletableSelected);
  }, [selectedDocuments, filteredDocuments, canDeleteDocuments]);

  // Bulk operations
  const handleBulkDelete = async () => {
    if (selectedDocuments.size === 0) return;
    
    const documentsToDelete = Array.from(selectedDocuments);
    const errors: string[] = [];
    let successCount = 0;

    for (const docId of documentsToDelete) {
      try {
        const response = await DatabaseService.deleteDocument(docId, user?.id);
        if (response.error) {
          errors.push(`Failed to delete document: ${response.error}`);
        } else {
          successCount++;
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        errors.push(`Failed to delete document: ${errorMessage}`);
      }
    }

    if (successCount > 0) {
      setMessage(`Successfully deleted ${successCount} document${successCount > 1 ? 's' : ''}`);
      setSelectedDocuments(new Set());
      setSelectAll(false);
      await loadDocuments();
      onDocumentUpdate();
    }

    if (errors.length > 0) {
      setError(`Some deletions failed: ${errors.join(', ')}`);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedDocuments.size === 0) return;
    
    const documentsToDownload = filteredDocuments.filter(doc => 
      selectedDocuments.has(doc.id) && canDownloadDocuments && doc.can_read
    );

    for (const doc of documentsToDownload) {
      await handleDownload(doc);
      // Add small delay to avoid overwhelming the browser
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  // Load documents on mount and when filters or deal change
  useEffect(() => {
    if (user?.id) {
      loadDocuments();
    }
  }, [filterType, dealId, selectedDealId, user?.id]);

  // Clear messages after timeout
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className={`flex flex-col h-full bg-white ${isMobile || isTablet ? '' : 'border-r border-gray-200'} ${className}`}>
      {/* Header */}
      <div className={`${isMobile ? 'p-3' : isTablet ? 'p-4' : 'p-4'} border-b border-gray-200`}>
        <div className={`flex items-center justify-between ${isMobile ? 'mb-3' : isTablet ? 'mb-4' : 'mb-4'}`}>
          <h2 className={`${isMobile ? 'text-base' : isTablet ? 'text-lg' : 'text-lg'} font-semibold text-gray-900 flex items-center gap-2`}>
            <FileText className={`${isMobile ? 'w-4 h-4' : isTablet ? 'w-5 h-5' : 'w-5 h-5'} text-blue-600`} />
            Documents
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={loadDocuments}
              disabled={isLoading}
              className={`${isMobile ? 'p-2' : 'p-1'} text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50`}
              title="Refresh"
            >
              <RefreshCw className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            {canUploadDocuments && (
              <>
                <button
                  onClick={() => {
                    onUploadClick();
                    setShowBulkUploadModal(true);
                  }}
                  className={`${isMobile ? 'p-2' : 'p-1'} text-blue-600 hover:text-blue-800 transition-colors`}
                  title="Upload Document"
                >
                </button>
                <button
                  onClick={() => setShowBulkUploadModal(true)}
                  className={`${isMobile ? 'p-2' : 'p-1'} text-green-600 hover:text-green-800 transition-colors`}
                  title="Bulk Upload"
                >
                  <Upload className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Bulk Actions Toolbar */}
        {selectedDocuments.size > 0 && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-800">
                {selectedDocuments.size} document{selectedDocuments.size > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2">
                {canDownloadDocuments && (
                  <button
                    onClick={handleBulkDownload}
                    className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
                  >
                    <Download className="w-3 h-3" />
                    Download All
                  </button>
                )}
                {canDeleteDocuments && (
                  <button
                    onClick={handleBulkDelete}
                    className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete All
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedDocuments(new Set());
                    setSelectAll(false);
                  }}
                  className="px-3 py-1.5 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-3">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 ${isMobile ? 'w-4 h-4' : 'w-4 h-4'}`} />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 ${isMobile ? 'py-3' : 'py-2'} text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        {/* Deal Selector - Only show if no dealId prop is provided (global documents page) */}
        {!dealId && availableDeals.length > 0 && (
          <div className="mb-3">
            <select
              value={selectedDealId || ''}
              onChange={(e) => setSelectedDealId(e.target.value || null)}
              className={`w-full px-3 ${isMobile ? 'py-3' : 'py-1.5'} text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              <option value="">All Deals</option>
              {availableDeals.map((deal) => (
                <option key={deal.deal_id} value={deal.deal_id}>
                  {deal.business_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Filters */}
        <div className={`${isMobile ? 'space-y-3' : 'space-y-2'}`}>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as DocumentType | 'all')}
            className={`w-full px-3 ${isMobile ? 'py-3' : 'py-1.5'} text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Types</option>
            <option value="tax_return">Tax Return</option>
            <option value="profit_loss_statement">Profit & Loss</option>
            <option value="balance_sheet">Balance Sheet</option>
            <option value="inventory_list">Inventory List</option>
            <option value="lease_agreement">Lease Agreement</option>
            <option value="financial_statement">Financial Statement</option>
            <option value="bank_statement">Bank Statement</option>
            <option value="insurance_document">Insurance</option>
            <option value="legal_document">Legal Document</option>
            <option value="other">Other</option>
          </select>

          <select
            value={filterAccessLevel}
            onChange={(e) => setFilterAccessLevel(e.target.value as DocumentAccessLevel | 'all')}
            className={`w-full px-3 ${isMobile ? 'py-3' : 'py-1.5'} text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Access Levels</option>
            <option value="private">Private</option>
            <option value="role_based">Role-Based</option>
            <option value="shared">Shared</option>
            <option value="public">Public</option>
          </select>

          {!buyerId && (
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as 'all' | 'buyer' | 'seller')}
              className={`w-full px-3 ${isMobile ? 'py-3' : 'py-1.5'} text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              <option value="all">All Categories</option>
              <option value="buyer">Buyer Documents</option>
              <option value="seller">Seller Documents</option>
            </select>
          )}
        </div>
        
        {/* Select All Checkbox */}
        {canDeleteDocuments && filteredDocuments.some(doc => doc.can_admin) && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Select all deletable documents
            </label>
          </div>
        )}
      </div>

      {/* Messages */}
      {message && (
        <div className="mx-4 mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <p className="text-sm text-green-800">{message}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mx-4 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
            <span className="ml-2 text-sm text-gray-600">Loading documents...</span>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="text-center py-8 px-4">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <h3 className="text-sm font-medium text-gray-900 mb-1">No documents found</h3>
            <p className="text-xs text-gray-600 mb-3">
              {searchTerm || filterType !== 'all' || filterAccessLevel !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Upload your first document to get started.'
              }
            </p>
            {canUploadDocuments && (
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => {
                    onUploadClick();
                    setShowBulkUploadModal(true);
                  }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  Upload Document
                </button>
                <button
                  onClick={() => setShowBulkUploadModal(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Upload className="w-3 h-3" />
                  Bulk Upload
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className={`${isMobile ? 'p-1' : 'p-2'}`}>
            {filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className={`${isMobile ? 'p-4' : 'p-3'} rounded-lg transition-colors mb-2 ${
                  selectedDocument?.id === doc.id
                    ? 'bg-blue-50 border border-blue-200'
                    : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    {/* Multi-select checkbox */}
                    {canDeleteDocuments && doc.can_admin && (
                      <div className="pt-1">
                        <input
                          type="checkbox"
                          checked={selectedDocuments.has(doc.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectDocument(doc.id, e.target.checked);
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    
                    {/* Document content - clickable area */}
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => onDocumentSelect(doc)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {doc.title}
                          </h4>
                        </div>
                        
                        {doc.description && (
                          <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                            {doc.description}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                          <span>{getDocumentTypeLabel(doc.document_type)}</span>
                          <span>•</span>
                          <span>{formatFileSize(doc.file_size)}</span>
                          <span>•</span>
                          <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                        </div>
                        
                        {(doc.uploaded_by || doc.owner_id) && (
                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                            <Users className="w-3 h-3" />
                            <span>Uploaded by: {doc.uploaded_by || doc.owner_id}</span>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Document Tags */}
                          {doc.document_tags && doc.document_tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {doc.document_tags
                                .map((tag, index) => (
                                <span
                                  key={index}
                                  className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                    tag.color === 'blue' 
                                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                      : 'bg-gray-100 text-gray-600 border border-gray-200'
                                  }`}
                                >
                                  {tag.label}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          
                          {/* Quick Actions */}
                          <div className="flex items-center gap-1">
                            {canDownloadDocuments && doc.can_read && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(doc);
                                }}
                                className={`${isMobile ? 'p-2' : 'p-1'} text-blue-600 hover:text-blue-800 transition-colors`}
                                title="Download"
                              >
                                <Download className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
                              </button>
                            )}
                            
                            {doc.can_read && onExtractionClick && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onExtractionClick(doc);
                                }}
                                className={`${isMobile ? 'p-2' : 'p-1'} text-purple-600 hover:text-purple-800 transition-colors`}
                                title="Extract Data"
                              >
                                <Brain className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
                              </button>
                            )}
                            
                            {canShareDocuments && doc.can_admin && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // TODO: Open share modal
                                }}
                                className={`${isMobile ? 'p-2' : 'p-1'} text-green-600 hover:text-green-800 transition-colors`}
                                title="Share"
                              >
                                <Share2 className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
                              </button>
                            )}
                            
                            {canEditDocuments && doc.can_admin && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(doc);
                                }}
                                className={`${isMobile ? 'p-2' : 'p-1'} text-blue-600 hover:text-blue-800 transition-colors`}
                                title="Edit"
                              >
                                <Edit className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
                              </button>
                            )}
                            
                            {canDeleteDocuments && doc.can_admin && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(doc);
                                }}
                                className={`${isMobile ? 'p-2' : 'p-1'} text-red-600 hover:text-red-800 transition-colors`}
                                title="Delete"
                              >
                                <Trash2 className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onUploadSuccess={() => {
          setShowBulkUploadModal(false);
          loadDocuments();
          onDocumentUpdate();
        }}
        buyerId={buyerId}
        dealId={dealId}
      />
    </div>
  );
}
